import React, { useEffect, useRef, useState } from 'react';
import { Sparkles, Building2, MessageSquareText, UserPlus, User, ListChecks, Check, CheckCircle2, RotateCcw, Loader2 } from 'lucide-react';
import { DEMO_SAMPLES } from './demoSamples';

// The landing page's hands-on demo of AI capture: pick a note, watch the
// draft appear, approve it. It plays pre-written drafts (demoSamples.js), so
// it makes no request at all; what it shows is the shape of a real draft.

const STEPS = ['Reading the note', 'Matching the account', 'Finding the people', 'Drafting next steps'];

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

export function draftSummary(draft) {
  const newContacts = draft.contacts.filter(c => c.isNew).length;
  const parts = ['1 activity'];
  if (newContacts) parts.push(plural(newContacts, 'new contact', 'new contacts'));
  parts.push(plural(draft.actionItems.length, 'action item', 'action items'));
  const list = parts.length > 2 ? `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}` : parts.join(' and ');
  return draft.account.isNew
    ? `New account ${draft.account.name}, with ${list}.`
    : `${list.charAt(0).toUpperCase()}${list.slice(1)} on ${draft.account.name}.`;
}

export default function CaptureDemo({ draftDelay = 1800 }) {
  const [sampleKey, setSampleKey] = useState(DEMO_SAMPLES[0].key);
  const [phase, setPhase] = useState('idle'); // idle | drafting | drafted | approved
  const [step, setStep] = useState(0);
  const timers = useRef([]);
  const sample = DEMO_SAMPLES.find(s => s.key === sampleKey);
  const { draft } = sample;

  const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = []; };
  useEffect(() => clearTimers, []);

  const pick = (key) => {
    clearTimers();
    setSampleKey(key);
    setPhase('idle');
    setStep(0);
  };

  const run = () => {
    clearTimers();
    setPhase('drafting');
    setStep(0);
    const tick = draftDelay / STEPS.length;
    for (let i = 1; i < STEPS.length; i += 1) {
      timers.current.push(setTimeout(() => setStep(i), tick * i));
    }
    timers.current.push(setTimeout(() => setPhase('drafted'), draftDelay));
  };

  const showDraft = phase === 'drafted' || phase === 'approved';

  return (
    <div className="lp-demo">
      <div className="lp-demo-tabs" role="tablist" aria-label="Sample notes">
        {DEMO_SAMPLES.map(s => (
          <button
            key={s.key}
            type="button"
            role="tab"
            aria-selected={s.key === sampleKey}
            className={`lp-demo-tab${s.key === sampleKey ? ' is-active' : ''}`}
            onClick={() => pick(s.key)}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="lp-demo-grid">
        <div className="lp-demo-note">
          <div className="lp-demo-label"><MessageSquareText size={16} aria-hidden="true" /> Your note, as you typed or said it</div>
          <p className="lp-demo-note-text">{sample.note}</p>
          {phase === 'idle' && (
            <button type="button" className="btn btn-primary lp-demo-run" onClick={run}>
              <Sparkles size={18} aria-hidden="true" /> Draft it
            </button>
          )}
          {phase !== 'idle' && (
            <button type="button" className="btn btn-secondary lp-demo-run" onClick={() => pick(sampleKey)}>
              <RotateCcw size={16} aria-hidden="true" /> Start over
            </button>
          )}
        </div>

        <div className={`lp-demo-draft lp-demo-draft--${phase}`} aria-live="polite">
          {phase === 'idle' && (
            <div className="lp-demo-empty">
              <Sparkles size={28} aria-hidden="true" />
              <p>Press <strong>Draft it</strong> to see what ActiveOptics proposes from this note.</p>
            </div>
          )}

          {phase === 'drafting' && (
            <div className="lp-demo-empty">
              <Loader2 size={28} className="lp-spin" aria-hidden="true" />
              <p>{STEPS[step]}…</p>
            </div>
          )}

          {showDraft && (
            <>
              <div className="lp-draft-head">
                <span className="lp-draft-badge"><Sparkles size={14} aria-hidden="true" /> Draft</span>
                <span className="lp-draft-hint">{phase === 'approved' ? 'Approved' : 'Nothing is saved until you approve'}</span>
              </div>

              <div className="lp-draft-block" style={{ '--i': 0 }}>
                <div className="lp-draft-title"><Building2 size={16} aria-hidden="true" /> Account</div>
                <div className="lp-draft-row">
                  <span>{draft.account.name}</span>
                  <span className={`lp-pill${draft.account.isNew ? ' lp-pill--new' : ''}`}>{draft.account.isNew ? 'New account' : 'Matched'}</span>
                </div>
              </div>

              <div className="lp-draft-block" style={{ '--i': 1 }}>
                <div className="lp-draft-title"><MessageSquareText size={16} aria-hidden="true" /> Activity · {draft.activity.type}</div>
                <p className="lp-draft-text">{draft.activity.summary}</p>
              </div>

              <div className="lp-draft-block" style={{ '--i': 2 }}>
                <div className="lp-draft-title"><UserPlus size={16} aria-hidden="true" /> Contacts</div>
                <ul className="lp-draft-list">
                  {draft.contacts.map(c => (
                    <li key={c.name} className="lp-draft-row">
                      <span><User size={14} aria-hidden="true" /> <span>{c.name}<span className="lp-muted">, {c.title}</span></span></span>
                      <span className={`lp-pill${c.isNew ? ' lp-pill--new' : ''}`}>{c.isNew ? 'New' : 'Matched'}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="lp-draft-block" style={{ '--i': 3 }}>
                <div className="lp-draft-title"><ListChecks size={16} aria-hidden="true" /> Action items</div>
                <ul className="lp-draft-list">
                  {draft.actionItems.map(a => (
                    <li key={a.title} className="lp-draft-row">
                      <span><Check size={14} aria-hidden="true" /> {a.title}</span>
                      <span className="lp-muted">{a.due}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {phase === 'drafted' ? (
                <div className="lp-draft-actions" style={{ '--i': 4 }}>
                  <button type="button" className="btn btn-primary" onClick={() => setPhase('approved')}>
                    <Check size={18} aria-hidden="true" /> Approve and save
                  </button>
                </div>
              ) : (
                <div className="lp-draft-done" role="status">
                  <CheckCircle2 size={20} aria-hidden="true" />
                  <div>
                    <strong>{draftSummary(draft)}</strong>
                    <span>In the app these land on the account in one click. This demo saves nothing.</span>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
